import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'

import { format as formatFns } from 'date-fns'
import classnames from 'classnames'

import {
  withLocalizeProps,
  type LocalizeProps,
} from '@/modules/shared/context/localize/LocalizeContext'

import { MessageModel } from 'app/models/messageModel'
import { ExchangeRateConfigurationModel } from 'app/models/configuration/exchangeRateConfigurationModel'
import { MessageType } from 'app/constants/messageType'

import { ExchangeRateSourceType } from 'app/constants/configuration/exchangeRateSourceType'

import { OperationResultType } from 'app/constants/operationResultType'
import {
  faCalendar,
  faDownload,
  faEuro,
} from '@fortawesome/free-solid-svg-icons'
import { ContentHeaderBar } from '../../components/ContentHeaderBar'
import { DatePickerSingleButton } from './DatePickerSingleButton'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { ExchangeRateModal } from './ui/ExchangeRateModal'
import { ExchangeRateContainer } from './ui/ExchangeRateContainer'

export type ExchangeRateConfigurationStateProps = {
  companyId: string
  configuration: Array<ExchangeRateConfigurationModel>
  isLoading: boolean
}

export type ExchangeRateConfigurationDispatchProps = {
  showMessage: (message: MessageModel) => void
  getExchangeRateConfiguration: (
    companyId: string,
    rateDate: any,
    source: number,
  ) => Promise<ReadonlyArray<ExchangeRateConfigurationModel>>
  downloadExchangeRates: (
    companyId: string,
    dateFrom: any,
    dateTo: any,
    source: number,
  ) => Promise<any>
}

export type ExchangeRateConfigurationOwnProps = {
  companyId: string
}

function formatDate(date, format) {
  return formatFns(date, format)
}

type ExchangeRateConfigurationProps = ExchangeRateConfigurationStateProps &
  ExchangeRateConfigurationDispatchProps &
  ExchangeRateConfigurationOwnProps

type ExchangeRateConfigurationState = {
  configuration: Array<ExchangeRateConfigurationModel>
  canSave: boolean
  exchangeRateDate: Moment
  dateFrom: Moment
  dateTo: Moment
  sourceType: ExchangeRateSourceType
  showModal: boolean
  page: number
  perPage: number
  pending: boolean
  phrase: string
}

class ExchangeRateConfiguration extends React.Component<
  ExchangeRateConfigurationProps & LocalizeProps,
  ExchangeRateConfigurationState
> {
  constructor(props: ExchangeRateConfigurationProps & LocalizeProps) {
    super(props)

    this.exchangeRateDatePickerRef = React.createRef()
    this.dateFromPickerRef = React.createRef()
    this.dateToPickerRef = React.createRef()

    this.state = {
      configuration: props.configuration,
      canSave: false,
      exchangeRateDate: moment(),
      dateFrom: moment(),
      dateTo: moment(),
      sourceType: ExchangeRateSourceType.Nbp,
      showModal: false,
      page: 1,
      perPage: 10,
      pending: false,
      phrase: '',
    }
  }

  private exchangeRateDatePickerRef: any
  private dateFromPickerRef: any
  private dateToPickerRef: any

  componentDidMount() {
    if (this.props.companyId != null) {
      this.props.getExchangeRateConfiguration(
        this.props.companyId,
        this.state.exchangeRateDate,
        this.state.sourceType,
      )
    }
  }

  componentWillReceiveProps(nextProps: ExchangeRateConfigurationProps) {
    if (nextProps.companyId != this.props.companyId) {
      this.props.getExchangeRateConfiguration(
        nextProps.companyId,
        this.state.exchangeRateDate,
        this.state.sourceType,
      )
    }

    if (nextProps.configuration != this.props.configuration) {
      this.setState({ configuration: nextProps.configuration })
    }
  }

  translate = (text: string): string => {
    const translateText = this.props.translate
    return translateText(text) as string
  }

  filter = () => {
    const rows = this.state.configuration || []
    const phrase = this.state.phrase?.toLowerCase().trim()

    if (!phrase) return rows

    return rows.filter((r) =>
      [
        r.currency.code,
        r.currency.id,
        r.currency.name,
        r.exchangeRate,
        r.tableName,
      ]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(phrase)),
    )
  }

  downloadExchangeRates = async (dateFrom: Moment, dateTo: Moment) => {
    const result = await this.props.downloadExchangeRates(
      this.props.companyId,
      dateFrom,
      dateTo,
      this.state.sourceType,
    )
    if (
      result != true &&
      result.some(
        (r) =>
          r.type == OperationResultType.Warning ||
          r.type == OperationResultType.Error,
      )
    ) {
      this.props.showMessage({
        text: this.props
          .translate('configuration.exchangeRates.download-error')
          .toString(),
        details: this.props.translate(`errorCode.${result[0].code}`).toString(),
        type: MessageType.ERROR,
      })
    } else {
      this.props.showMessage({
        text: this.props
          .translate('configuration.exchangeRates.download-rates-success-title')
          .toString(),
        details: this.props
          .translate(
            'configuration.exchangeRates.download-rates-success-details',
          )
          .toString(),
        type: MessageType.INFO,
      })

      this.props.getExchangeRateConfiguration(
        this.props.companyId,
        this.state.exchangeRateDate,
        this.state.sourceType,
      )
    }
  }

  _downloadExchangeRates = () => {
    if (this.state.sourceType == ExchangeRateSourceType.Nbp) {
      this.downloadExchangeRates(
        this.state.exchangeRateDate,
        this.state.exchangeRateDate,
      )
    } else {
      this.setState({ showModal: true })
    }
  }







  formatDate = (date: Date, format: string) => {
    return formatFns(date, format)
  }

  handleDownload = (start: string, end: string) => {
    this.downloadExchangeRates(moment(start), moment(end))
    this.setState({ showModal: false })
  }

  render() {
    const translate = this.props.translate

    const tabClass = (active: boolean) =>
      classnames(
        'tw-cursor-pointer tw-border-b-2 tw-pb-1 tw-text-sm tw-font-medium tw-tracking-wide tw-text-content-base tw-transition-colors',
        active ? 'tw-border-content-base' : 'tw-border-transparent',
      )

    if (this.state.configuration == null) {
      return null
    }

    return (
      <SettingsPageWrapper
        isReady={Boolean(this.state.configuration)}
        isPending={this.props.isLoading}
      >
        <ContentHeaderBar
          title={translate('configuration-menu.exchange-rates').toString()}
          titleIcon={faEuro}
          actions={[
            (this.state.sourceType == ExchangeRateSourceType.Nbp ||
              this.state.sourceType == ExchangeRateSourceType.Ecb) && {
              icon: faDownload,
              title: translate(
                'newDelegation.download-exchange-rate',
              ).toString(),
              onClick: async () => {
                this.setState({ showModal: true })
              },
            },
          ]}
          middle={
            <>
              <span
                className={tabClass(
                  this.state.sourceType === ExchangeRateSourceType.Nbp,
                )}
                onClick={() => {
                  this.setState({
                    page: 1,
                    sourceType: ExchangeRateSourceType.Nbp,
                  })
                  this.props.getExchangeRateConfiguration(
                    this.props.companyId,
                    this.state.exchangeRateDate,
                    ExchangeRateSourceType.Nbp,
                  )
                }}
              >
                {this.translate('configuration.exchangeRateSourceType.Nbp')}
              </span>
              <span
                className={tabClass(
                  this.state.sourceType === ExchangeRateSourceType.QuarterWge,
                )}
                onClick={() => {
                  this.setState({
                    page: 1,
                    sourceType: ExchangeRateSourceType.QuarterWge,
                  })
                  this.props.getExchangeRateConfiguration(
                    this.props.companyId,
                    this.state.exchangeRateDate,
                    ExchangeRateSourceType.QuarterWge,
                  )
                }}
              >
                {this.translate(
                  'configuration.exchangeRateSourceType.QuarterWge',
                )}
              </span>
              <span
                className={tabClass(
                  this.state.sourceType === ExchangeRateSourceType.Ecb,
                )}
                onClick={() => {
                  this.setState({
                    page: 1,
                    sourceType: ExchangeRateSourceType.Ecb,
                  })
                  this.props.getExchangeRateConfiguration(
                    this.props.companyId,
                    this.state.exchangeRateDate,
                    ExchangeRateSourceType.Ecb,
                  )
                }}
              >
                {this.translate('configuration.exchangeRateSourceType.Ecb')}
              </span>
            </>
          }
        >
          <>
            {this.state.sourceType == ExchangeRateSourceType.Nbp && (
              <DatePickerSingleButton
                value={formatDate(
                  this.state.exchangeRateDate.toDate(),
                  'YYYY-MM-DD',
                )}
                setValue={(dateStr: string) => {
                  const d = new Date(dateStr)
                  const newDate = moment(d)
                  this.setState({ exchangeRateDate: newDate })
                  this.props.getExchangeRateConfiguration(
                    this.props.companyId,
                    newDate,
                    0,
                  )
                }}
              />
            )}
          </>
        </ContentHeaderBar>

        <ExchangeRateContainer
          exchangeRateDate={this.state.exchangeRateDate.format('YYYY-MM-DD')}
          rates={this.state.configuration}
          sourceType={this.state.sourceType}
          handleDownloadExchangeRates={this._downloadExchangeRates}
        />


        <ExchangeRateModal
          isOpen={this.state.showModal}
          handleClose={() => this.setState({ showModal: false })}
          sourceType={this.state.sourceType}
          handleDownload={this.handleDownload}
        />
      </SettingsPageWrapper>
    )
  }
}

export default withLocalizeProps(ExchangeRateConfiguration)
